/**
 * Enrolamiento (docs/esquema.md §5.4, lado cliente): {request_id, codigo,
 * nombre} se PERSISTEN antes del POST — un crash entre el consumo del código
 * en el server y el guardado local se recupera reintentando con lo persistido
 * (el server rota el token del MISMO dispositivo). Recién cuando token +
 * catálogos quedaron durables se borra el pendiente.
 */

import { generarUlid } from '../../src/ulid';
import { alCompletar, leerConfig, type CatalogosLocales, type EnrolamientoPendiente, type Sesion } from './bd';

export type ResultadoEnrolamiento = { ok: true; estacion: string | null } | { ok: false; error: string; reintentable: boolean };

interface RespuestaEnrolado {
	token: string;
	dispositivo_id: string;
	estacion: string | null;
	persona_habitual_id?: string | null;
	hora_servidor: string;
	/** v6 (§12.7): ausente en un servidor anterior. */
	arranque_id?: string;
	catalogos: CatalogosLocales;
}

export async function enrolar(
	bd: IDBDatabase,
	codigo: string,
	nombre: string,
	traer: typeof fetch = fetch,
): Promise<ResultadoEnrolamiento> {
	// Reusar el request_id persistido SOLO si es el mismo código (un código nuevo
	// es un intento nuevo); persistir ANTES del POST.
	const pendiente = await leerConfig<EnrolamientoPendiente>(bd, 'enrolamiento');
	const requestId = pendiente && pendiente.codigo === codigo ? pendiente.request_id : generarUlid();
	{
		const tx = bd.transaction('config', 'readwrite');
		tx.objectStore('config').put({ request_id: requestId, codigo, nombre } satisfies EnrolamientoPendiente, 'enrolamiento');
		await alCompletar(tx);
	}

	let respuesta: Response;
	const t0 = Date.now();
	try {
		respuesta = await traer('/api/dispositivos/enrolar', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ codigo, nombre_dispositivo: nombre, request_id: requestId }),
		});
	} catch {
		return { ok: false, error: 'Sin señal. El intento queda guardado: probá de nuevo cuando haya conexión.', reintentable: true };
	}
	const t1 = Date.now();

	if (respuesta.status === 403) {
		// Código inválido/vencido/usado: el pendiente se descarta (un reintento con
		// otro código necesita request_id propio).
		const tx = bd.transaction('config', 'readwrite');
		tx.objectStore('config').delete('enrolamiento');
		await alCompletar(tx);
		return { ok: false, error: 'Código inválido o vencido. Pedile uno nuevo al encargado.', reintentable: false };
	}
	if (respuesta.status === 429) {
		return { ok: false, error: 'Demasiados intentos. Esperá un minuto y probá de nuevo.', reintentable: true };
	}
	if (respuesta.status === 411 || respuesta.status === 413) {
		// El navegador siempre declara el largo y el cuerpo ronda los 150 bytes: si el servidor
		// lo rechaza así, el problema es de la app, no del código. Se dice, no se disfraza de red.
		return { ok: false, error: `El servidor rechazó el envío (${respuesta.status}). Es un problema de la app, no del código: avisá a la oficina.`, reintentable: true };
	}
	if (!respuesta.ok) {
		return { ok: false, error: 'No se pudo enrolar. Probá de nuevo en un rato.', reintentable: true };
	}

	const cuerpo = (await respuesta.json().catch(() => null)) as RespuestaEnrolado | null;
	if (!cuerpo || typeof cuerpo.token !== 'string' || typeof cuerpo.dispositivo_id !== 'string' || !cuerpo.catalogos) {
		return { ok: false, error: 'Respuesta inesperada del servidor. Probá de nuevo.', reintentable: true };
	}

	// Todo durable en una transacción; recién entonces desaparece el pendiente.
	const tx = bd.transaction(['config', 'catalogos', 'outbox'], 'readwrite');
	const config = tx.objectStore('config');
	config.put(
		{
			token: cuerpo.token,
			dispositivo_id: cuerpo.dispositivo_id,
			estacion: cuerpo.estacion,
			persona_habitual_id: typeof cuerpo.persona_habitual_id === 'string' ? cuerpo.persona_habitual_id : null,
			nombre_dispositivo: nombre,
			enrolado_en: cuerpo.hora_servidor,
		} satisfies Sesion,
		'sesion',
	);
	config.delete('enrolamiento');
	// v6 (§12.7): el arranque al que nace adherido el teléfono, junto con la sesión.
	if (typeof cuerpo.arranque_id === 'string') config.put(cuerpo.arranque_id, 'arranque');
	// EXCEPCIÓN declarada a «epoch nuevo solo con outbox vacío» (C1 de la ronda
	// del diff): enrolarse es una identidad nueva de dispositivo — las capturas
	// futuras arrancan en un epoch propio. Las filas viejas conservan SU epoch
	// (jamás se re-etiquetan) y quedan congeladas hasta que el encargado decida.
	const filasViejas = (await new Promise<Array<{ epoch: string }>>((resolver) => {
		const pedido = tx.objectStore('outbox').getAll();
		pedido.onsuccess = () => resolver(pedido.result as Array<{ epoch: string }>);
		pedido.onerror = () => resolver([]);
	})) as Array<{ epoch: string }>;
	const epochsViejos = [...new Set(filasViejas.map((f) => f.epoch))];
	config.put({ epoch: generarUlid(), proximo_seq: 1 }, 'cola');
	const preferencias = ((await new Promise((resolver) => {
		const pedido = config.get('preferencias');
		pedido.onsuccess = () => resolver(pedido.result ?? {});
		pedido.onerror = () => resolver({});
	})) ?? {}) as Record<string, unknown>;
	config.put(
		{
			...preferencias,
			desenrolado: false,
			catalogos_bajados_en: Date.now(),
			// Desvío del reloj medido YA, en el propio enrolamiento (A4): la primera
			// captura no puede correr con un reloj sin calibrar.
			desvio_ms: Date.parse(cuerpo.hora_servidor) - (t0 + t1) / 2,
			hora_ultimo_sync: cuerpo.hora_servidor,
			// La marca del snapshot, como la escribe el refresco (§5.1): sin ella, hasta el primer refresco la
			// proyección no sabía si un acuse propio era anterior o posterior al catálogo del enrolamiento
			// (Codex, segunda ronda del plan de verde reciente, 5/10).
			catalogos_hora_servidor: cuerpo.hora_servidor,
			epochs_congelados: epochsViejos,
		},
		'preferencias',
	);
	tx.objectStore('catalogos').put(cuerpo.catalogos, 'catalogos');
	await alCompletar(tx);

	return { ok: true, estacion: cuerpo.estacion };
}

/** Al arrancar: si quedó un enrolamiento a medias (crash post-POST), reintentarlo. */
export async function reintentarEnrolamientoPendiente(bd: IDBDatabase, traer: typeof fetch = fetch): Promise<ResultadoEnrolamiento | null> {
	const pendiente = await leerConfig<EnrolamientoPendiente>(bd, 'enrolamiento');
	const sesion = await leerConfig<Sesion>(bd, 'sesion');
	if (!pendiente || sesion) return null;
	return enrolar(bd, pendiente.codigo, pendiente.nombre, traer);
}
